import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import "./PaymentSuccess.css";
import { checkAvailability } from "../carAvailability/carAvailabilityThunk";
import ClipLoader from "react-spinners/ClipLoader";
import { saveBooking } from "../booking/bookingsThunk";
import { getFirestore, collection, addDoc } from "firebase/firestore";

function PaymentSuccess() {
  const location = useLocation();
  const dispatch = useDispatch();
  const [paymentStatus, setPaymentStatus] = useState("");
  const [hasVerified, setHasVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const nav = useNavigate();
  const bookingDetails = useSelector((state) => state.bookings);
  const carAvailabilityDetails = useSelector((state) => state.carAvailability);

  const userDetails = useMemo(
    () => ({
      email: bookingDetails.email,
      cellphone: bookingDetails.cellphone,
      country: bookingDetails.country,
      date: carAvailabilityDetails.date,
      session: carAvailabilityDetails.session,
      people: carAvailabilityDetails.people,
      name: bookingDetails.name,
      totalPrice: bookingDetails.totalPrice,
      pickupPoint: carAvailabilityDetails.pickupPoint, // Include pickup point here
    }),
    [bookingDetails, carAvailabilityDetails]
  );

  const handleSuccessfulPayment = useCallback(async () => {
    setIsLoading(false);
    setPaymentStatus("Payment was successful!");
    const availabilityResult = await dispatch(checkAvailability());

    if (availabilityResult.payload?.status === "success") {
      if (userDetails) {
        console.log(userDetails);
        dispatch(saveBooking(userDetails));

        //get firestore instance
        const db = getFirestore();

        // Construct the email details
        const emailData = {
          to: userDetails.email, // Recipient's email
          message: {
            subject: "Booking Confirmation - Yavola Tours",
            html: `<p>Hello ${userDetails.name},</p>
                 <p>Your booking for ${userDetails.date} was successful.</p>
                 <p>Total Price: ${userDetails.totalPrice}</p>
              <p> Pickup Point: ${userDetails.pickupPoint}</p>`,
            // Customize your email body as needed
          },
        };

        // Construct email details for business owner
        const ownerEmailData = {
          to: "bookings@yavolatours.com",
          message: {
            subject: "New Booking - Yavola Tours",
            html: `<p>Hello Business Owner,</p>
                 <p>A new booking has been made by ${userDetails.name} for ${userDetails.date}.</p>
                 <p>Pickup Point: ${userDetails.pickupPoint}</p>
                 <p>Total Price: ${userDetails.totalPrice}</p>`,
          },
        };

        try {
          const mailCollectionRef = collection(db, "mail");
          const docRef = await addDoc(mailCollectionRef, emailData);
          console.log("email trigger document with ID:", docRef.id);

          const ownerDocRef = await addDoc(
            collection(db, "mail"),
            ownerEmailData
          );
          console.log(
            "Business owner email trigger document with ID:",
            ownerDocRef.id
          );
        } catch (error) {
          console.error("error triggering email", error);
        }
      }
      nav("/", { replace: true });
    } else {
      setPaymentStatus("Payment verification failed. Please contact support.");
    }
  }, [dispatch, nav, userDetails]);

  useEffect(() => {
    const verifyPayment = async (transactionToken, retryCount = 0) => {
      setIsLoading(true);
      console.log("Verifying payment with token:", transactionToken);
      try {
        const response = await axios.post(
          "https://us-central1-yavola-8ca8d.cloudfunctions.net/verifyTransaction",
          { token: transactionToken }
        );

        if (response.data.success) {
          handleSuccessfulPayment();
        } else {
          setPaymentStatus(
            "Payment verification failed. Please contact support."
          );
        }
      } catch (error) {
        if (error) {
          // If a rate limit error is encountered, retry after a delay
          const maxRetries = 100;
          if (retryCount < maxRetries) {
            const retryDelay = 1000; // 5 seconds
            console.log(
              `Rate limit hit, retrying after ${retryDelay / 1000} seconds...`
            );
            setTimeout(
              () => verifyPayment(transactionToken, retryCount + 1),
              retryDelay
            );
          } else {
            console.error("Max retries reached. Please try again later.");
            setPaymentStatus("Service is busy. Please try again later.");
          }
        } else {
          // Handle other types of errors here
          console.error("Unexpected error during payment verification:", error);
          setPaymentStatus(
            "An unexpected error occurred. Please contact support."
          );
        }
      }
    };

    if (!hasVerified) {
      const params = new URLSearchParams(location.search);
      const transactionToken = params.get("TransactionToken");
      if (transactionToken) {
        setHasVerified(true);
        verifyPayment(transactionToken);
      } else {
        setPaymentStatus("Invalid transaction. No token provided.");
      }
    }
  }, [
    location,
    dispatch,
    nav,
    userDetails,
    hasVerified,
    handleSuccessfulPayment,
  ]);

  return (
    <div className="payment-success-container">
      <h1>Payment Status</h1>
      {isLoading ? (
        <ClipLoader size={50} color={"#1C611C"} loading={isLoading} />
      ) : (
        <p
          className={
            paymentStatus === "Payment was successful!"
              ? "success-message"
              : "error-message"
          }
        >
          {paymentStatus}
        </p>
      )}
    </div>
  );
}

export default PaymentSuccess;
